<p-toast position="top-right" role="status" aria-live="polite" aria-atomic="true" />

<!-- Loading state -->
@if (isLoading()) {
  <div class="loading-container" role="status" aria-live="polite">
    <i class="pi pi-spin pi-spinner" style="font-size: 2rem; color: #00125b;" aria-hidden="true"></i>
    <p>Caricamento dati AXA...</p>
  </div>
}

<!-- Professional type not applicable — show communication-email flag only (SOL codes managed in the dedicated "Gestisci Codici SOL" section) -->
@if (!isLoading() && showSolCodeSection()) {
  <section class="surface-0 border-round p-5 mb-4 shadow-1" role="region" aria-labelledby="sol-section-heading">
    <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
      <i class="pi pi-list text-2xl" style="color: #00125b;" aria-hidden="true"></i>
      <h3 id="sol-section-heading" class="text-xl font-semibold m-0" style="color: #00125b;">Dati Integrativi</h3>
    </div>
    <div class="grid">
      <div class="field col-12 md:col-4">
        <label class="font-semibold block mb-2">
          Mail per comunicazione
          <span class="text-red-500" aria-hidden="true">*</span>
          <span class="visually-hidden">obbligatorio</span>
        </label>
        <div class="flex align-items-center gap-4 mt-1" role="radiogroup" aria-label="Mail per comunicazione">
          <div class="flex align-items-center gap-2">
            <p-radiobutton name="communicationEmailSimple" [value]="true" [(ngModel)]="formData().otherFields.communicationEmail" (ngModelChange)="onCommunicationEmailChange($event)" inputId="communicationEmailSimple-si" />
            <label for="communicationEmailSimple-si" class="cursor-pointer">Sì</label>
          </div>
          <div class="flex align-items-center gap-2">
            <p-radiobutton name="communicationEmailSimple" [value]="false" [(ngModel)]="formData().otherFields.communicationEmail" (ngModelChange)="onCommunicationEmailChange($event)" inputId="communicationEmailSimple-no" />
            <label for="communicationEmailSimple-no" class="cursor-pointer">No</label>
          </div>
        </div>
      </div>
    </div>
    <div class="flex justify-content-end pt-3 border-top-1 border-200 mt-2">
      <button
        pButton
        type="button"
        label="Salva"
        icon="pi pi-save"
        [loading]="isSaving()"
        [disabled]="isSaving()"
        (click)="saveSolCodeOnly()"
      ></button>
    </div>
  </section>
}

<!-- Professional type not applicable (full message, no SOL) -->
@if (!isLoading() && !isApplicable() && !showSolCodeSection()) {
  <div class="not-applicable-message" role="status" aria-live="polite">
    <i class="pi pi-info-circle" style="font-size: 2rem; color: #6b7280;" aria-hidden="true"></i>
    <p>Questa sezione non è applicabile per questa tipologia di fiduciario.</p>
  </div>
}

<!-- Main form -->
@if (!isLoading() && isApplicable()) {
  <form (ngSubmit)="save()" novalidate class="axa-form-container" aria-label="Dati operativi AXA">

    <!-- =================== SEZIONE 1: AUTHORITY =================== -->
    <section class="surface-0 border-round p-5 mb-4 shadow-1" role="region" aria-labelledby="authority-section-heading">
      <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
        <i class="pi pi-shield text-2xl" style="color: #00125b;" aria-hidden="true"></i>
        <h3 id="authority-section-heading" class="text-xl font-semibold m-0" style="color: #00125b;">Authority</h3>
      </div>

      <div class="grid">

        <!-- 1. Authority (dropdown) -->
        <div class="field col-12 md:col-4">
          <label for="authorityType" class="font-semibold block mb-2">
            Authority
            <span class="text-red-500" aria-hidden="true">*</span>
            <span class="visually-hidden">obbligatorio</span>
          </label>
          <p-select
            inputId="authorityType"
            [options]="authorityTypeOptions()"
            [(ngModel)]="formData().authority.authorityType"
            (ngModelChange)="onAuthorityTypeChange($event)"
            name="authorityType"
            optionLabel="label"
            optionValue="value"
            placeholder="Seleziona..."
            styleClass="w-full"
            [invalid]="isFieldInvalid('authorityType')"
            [attr.aria-required]="'true'"
            [attr.aria-describedby]="isFieldInvalid('authorityType') ? 'authorityType-error' : null"
          />
          @if (isFieldInvalid('authorityType')) {
            <small id="authorityType-error" class="axa-field-error block mt-1" role="alert">Campo obbligatorio</small>
          }
        </div>

        <!-- Authority Altro - campo testo libero -->
        @if (formData().authority.authorityType === 'OTHER') {
          <div class="field col-12 md:col-4">
            <label for="authorityTypeOther" class="font-semibold block mb-2">
              Specifica Authority
              <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <input
              id="authorityTypeOther"
              pInputText
              name="authorityTypeOther"
              [(ngModel)]="formData().authority.authorityTypeOther"
              placeholder="Specificare..."
              class="w-full"
              [class.ng-invalid]="isFieldInvalid('authorityTypeOther')"
              [attr.aria-required]="'true'"
              [attr.aria-describedby]="isFieldInvalid('authorityTypeOther') ? 'authorityTypeOther-error' : null"
            />
            @if (isFieldInvalid('authorityTypeOther')) {
              <small id="authorityTypeOther-error" class="axa-field-error block mt-1" role="alert">Campo obbligatorio</small>
            }
          </div>
        }

        <!-- 2. Nominativo Authority – Ragione Sociale -->
        <div class="field col-12 md:col-4">
          <label for="authorityName" class="font-semibold block mb-2">
            Nominativo Authority – Ragione Sociale
            <span class="text-red-500" aria-hidden="true">*</span>
            <span class="visually-hidden">obbligatorio</span>
          </label>
          <input
            id="authorityName"
            pInputText
            name="authorityName"
            [(ngModel)]="formData().authority.authorityName"
            placeholder="Ragione sociale o nominativo..."
            class="w-full"
            [class.ng-invalid]="isFieldInvalid('authorityName')"
            [attr.aria-required]="'true'"
            [attr.aria-describedby]="isFieldInvalid('authorityName') ? 'authorityName-error' : null"
          />
          @if (isFieldInvalid('authorityName')) {
            <small id="authorityName-error" class="axa-field-error block mt-1" role="alert">Campo obbligatorio</small>
          }
        </div>

        <!-- 3. Email (Nominativo Authority) -->
        <div class="field col-12 md:col-4">
          <label for="authorityEmail" class="font-semibold block mb-2">Email (Nominativo Authority)</label>
          <input
            id="authorityEmail"
            pInputText
            name="authorityEmail"
            type="email"
            [(ngModel)]="formData().authority.authorityEmail"
            placeholder="email@esempio.com"
            class="w-full"
            [class.ng-invalid]="isFieldInvalid('authorityEmail')"
            [attr.aria-describedby]="isFieldInvalid('authorityEmail') ? 'authorityEmail-error' : null"
          />
          @if (isFieldInvalid('authorityEmail')) {
            <small id="authorityEmail-error" class="axa-field-error block mt-1" role="alert">Formato email non valido</small>
          }
        </div>

        <!-- 4. Telefono (Nominativo Authority) -->
        <div class="field col-12 md:col-4">
          <label for="authorityPhone" class="font-semibold block mb-2">Telefono (Nominativo Authority)</label>
          <input
            id="authorityPhone"
            pInputText
            name="authorityPhone"
            type="tel"
            autocomplete="tel"
            [(ngModel)]="formData().authority.authorityPhone"
            placeholder="Es. 0212345678"
            class="w-full"
          />
        </div>

        @if (showHailFields()) {
          <!-- 18. Referente tecnico per Authority (solo HAIL_PROVIDER) -->
          <div class="field col-12 md:col-4">
            <label for="technicalReferent" class="font-semibold block mb-2">
              Referente tecnico per Authority
              <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <input
              id="technicalReferent"
              pInputText
              name="technicalReferent"
              [(ngModel)]="formData().authority.technicalReferent"
              (blur)="touchedFields.add('technicalReferent')"
              placeholder="Nome Cognome"
              class="w-full"
              [class.ng-invalid]="isFieldInvalid('technicalReferent')"
              [attr.aria-required]="'true'"
              [attr.aria-describedby]="isFieldInvalid('technicalReferent') ? 'technicalReferent-error' : null"
            />
            @if (isFieldInvalid('technicalReferent')) {
              <small id="technicalReferent-error" class="axa-field-error block mt-1" role="alert">Campo obbligatorio</small>
            }
          </div>

          <!-- 20. Certificazioni green (solo HAIL_PROVIDER) -->
          <div class="field col-12 md:col-4">
            <label for="greenCertifications" class="font-semibold block mb-2">Certificazioni green</label>
            <input
              id="greenCertifications"
              pInputText
              name="greenCertifications"
              [(ngModel)]="formData().authority.greenCertifications"
              placeholder="Descrizione libera"
              class="w-full"
            />
          </div>
        }

      </div>
    </section>

    <!-- =================== SEZIONE 2: ALTRI CAMPI (non per HAIL_PROVIDER) =================== -->
    @if (showOtherFields()) {
      <section class="surface-0 border-round p-5 mb-4 shadow-1" role="region" aria-labelledby="altri-campi-section-heading">
        <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
          <i class="pi pi-list text-2xl" style="color: #00125b;" aria-hidden="true"></i>
          <h3 id="altri-campi-section-heading" class="text-xl font-semibold m-0" style="color: #00125b;">Altri Campi</h3>
        </div>

        <div class="grid">

          <!-- Codice SOL -->
          <div class="field col-12 md:col-4">
            <label for="solCode" class="font-semibold block mb-2">
              Codice SOL
            </label>
            <input
              id="solCode"
              pInputText
              name="solCode"
              [(ngModel)]="formData().otherFields.solCode"
              placeholder="Codice SOL..."
              maxlength="5"
              class="w-full"
            />
          </div>

          <!-- 5. Network di origine -->
          <div class="field col-12 md:col-4">
            <label for="networkOrigin" class="font-semibold block mb-2">
              Network di origine
              <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <input
              id="networkOrigin"
              pInputText
              name="networkOrigin"
              [(ngModel)]="formData().otherFields.networkOrigin"
              placeholder="Network di origine..."
              class="w-full"
              [class.ng-invalid]="isFieldInvalid('networkOrigin')"
              [attr.aria-required]="'true'"
              [attr.aria-describedby]="isFieldInvalid('networkOrigin') ? 'networkOrigin-error' : null"
            />
            @if (isFieldInvalid('networkOrigin')) {
              <small id="networkOrigin-error" class="axa-field-error block mt-1" role="alert">Campo obbligatorio</small>
            }
          </div>

          <!-- 6. Visibilità WEB -->
          <div class="field col-12 md:col-4">
            <label class="font-semibold block mb-2">
              Visibilità WEB
              <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <div class="flex align-items-center gap-4 mt-1" role="radiogroup" aria-label="Visibilità WEB">
              <div class="flex align-items-center gap-2">
                <p-radiobutton name="webVisibility" [value]="true" [(ngModel)]="formData().otherFields.webVisibility" inputId="webVisibility-si" />
                <label for="webVisibility-si" class="cursor-pointer">Sì</label>
              </div>
              <div class="flex align-items-center gap-2">
                <p-radiobutton name="webVisibility" [value]="false" [(ngModel)]="formData().otherFields.webVisibility" inputId="webVisibility-no" />
                <label for="webVisibility-no" class="cursor-pointer">No</label>
              </div>
            </div>
          </div>

          <!-- 7. ARS -->
          <div class="field col-12 md:col-4">
            <label class="font-semibold block mb-2">
              ARS
              <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <div class="flex align-items-center gap-4 mt-1" role="radiogroup" aria-label="ARS">
              <div class="flex align-items-center gap-2">
                <p-radiobutton name="ars" [value]="true" [(ngModel)]="formData().otherFields.ars" inputId="ars-si" />
                <label for="ars-si" class="cursor-pointer">Sì</label>
              </div>
              <div class="flex align-items-center gap-2">
                <p-radiobutton name="ars" [value]="false" [(ngModel)]="formData().otherFields.ars" inputId="ars-no" />
                <label for="ars-no" class="cursor-pointer">No</label>
              </div>
            </div>
          </div>

          <!-- 8. Tier 1 -->
          <div class="field col-12 md:col-4">
            <label class="font-semibold block mb-2">
              Tier 1
              <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <div class="flex align-items-center gap-4 mt-1" role="radiogroup" aria-label="Tier 1">
              <div class="flex align-items-center gap-2">
                <p-radiobutton name="tier1" [value]="true" [(ngModel)]="formData().otherFields.tier1" inputId="tier1-si" />
                <label for="tier1-si" class="cursor-pointer">Sì</label>
              </div>
              <div class="flex align-items-center gap-2">
                <p-radiobutton name="tier1" [value]="false" [(ngModel)]="formData().otherFields.tier1" inputId="tier1-no" />
                <label for="tier1-no" class="cursor-pointer">No</label>
              </div>
            </div>
          </div>

          <!-- 9. estimator -->
          <div class="field col-12 md:col-4">
            <label class="font-semibold block mb-2">
              estimator
              <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <div class="flex align-items-center gap-4 mt-1" role="radiogroup" aria-label="estimator">
              <div class="flex align-items-center gap-2">
                <p-radiobutton name="estimator" [value]="true" [(ngModel)]="formData().otherFields.estimator" inputId="estimator-si" />
                <label for="estimator-si" class="cursor-pointer">Sì</label>
              </div>
              <div class="flex align-items-center gap-2">
                <p-radiobutton name="estimator" [value]="false" [(ngModel)]="formData().otherFields.estimator" inputId="estimator-no" />
                <label for="estimator-no" class="cursor-pointer">No</label>
              </div>
            </div>
          </div>

          <!-- 10. Alphscale Attive -->
          <div class="field col-12 md:col-4">
            <label class="font-semibold block mb-2">
              Alphscale Attive
              <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <div class="flex align-items-center gap-4 mt-1" role="radiogroup" aria-label="Alphscale Attive">
              <div class="flex align-items-center gap-2">
                <p-radiobutton name="alphscaleActive" [value]="true" [(ngModel)]="formData().otherFields.alphscaleActive" inputId="alphscaleActive-si" />
                <label for="alphscaleActive-si" class="cursor-pointer">Sì</label>
              </div>
              <div class="flex align-items-center gap-2">
                <p-radiobutton name="alphscaleActive" [value]="false" [(ngModel)]="formData().otherFields.alphscaleActive" inputId="alphscaleActive-no" />
                <label for="alphscaleActive-no" class="cursor-pointer">No</label>
              </div>
            </div>
          </div>

          <!-- 11. Data inserimento Albo -->
          <div class="field col-12 md:col-4">
            <label for="registryInsertionDate-input" class="font-semibold block mb-2">
              Data inserimento Albo
              <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <p-datepicker
              [(ngModel)]="formData().otherFields.registryInsertionDate"
              name="registryInsertionDate"
              dateFormat="dd/mm/yy"
              [maxDate]="today"
              [showIcon]="true"
              [showButtonBar]="true"
              placeholder="Seleziona data"
              inputId="registryInsertionDate-input"
              styleClass="w-full"
              (onBlur)="touchedFields.add('registryInsertionDate')"
              [invalid]="isFieldInvalid('registryInsertionDate')"
              [attr.aria-required]="'true'"
            />
            @if (isFieldInvalid('registryInsertionDate')) {
              <small id="registryInsertionDate-error" class="axa-field-error block mt-1" role="alert">Campo obbligatorio</small>
            }
          </div>

          <!-- 12. Data ultima rinegoziazione -->
          <div class="field col-12 md:col-4">
            <label for="lastRenegotiationDate-input" class="font-semibold block mb-2">
              Data ultima rinegoziazione
              <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <p-datepicker
              [(ngModel)]="formData().otherFields.lastRenegotiationDate"
              name="lastRenegotiationDate"
              dateFormat="dd/mm/yy"
              [maxDate]="today"
              [showIcon]="true"
              [showButtonBar]="true"
              placeholder="Seleziona data"
              inputId="lastRenegotiationDate-input"
              styleClass="w-full"
              (onBlur)="touchedFields.add('lastRenegotiationDate')"
              [invalid]="isFieldInvalid('lastRenegotiationDate')"
              [attr.aria-required]="'true'"
            />
            @if (isFieldInvalid('lastRenegotiationDate')) {
              <small id="lastRenegotiationDate-error" class="axa-field-error block mt-1" role="alert">Campo obbligatorio</small>
            }
          </div>

          <!-- 13. SC -->
          <div class="field col-12 md:col-4">
            <label for="sc" class="font-semibold block mb-2">
              SC
              <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <input
              id="sc"
              pInputText
              name="sc"
              [(ngModel)]="formData().otherFields.sc"
              placeholder="SC..."
              class="w-full"
              [class.ng-invalid]="isFieldInvalid('sc')"
              [attr.aria-required]="'true'"
              [attr.aria-describedby]="isFieldInvalid('sc') ? 'sc-error' : null"
            />
            @if (isFieldInvalid('sc')) {
              <small id="sc-error" class="axa-field-error block mt-1" role="alert">Campo obbligatorio</small>
            }
          </div>

          <!-- 14. Referente SC -->
          <div class="field col-12 md:col-4">
            <label for="scReferent" class="font-semibold block mb-2">
              Referente SC
              <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <input
              id="scReferent"
              pInputText
              name="scReferent"
              [(ngModel)]="formData().otherFields.scReferent"
              placeholder="Referente SC..."
              class="w-full"
              [class.ng-invalid]="isFieldInvalid('scReferent')"
              [attr.aria-required]="'true'"
              [attr.aria-describedby]="isFieldInvalid('scReferent') ? 'scReferent-error' : null"
            />
            @if (isFieldInvalid('scReferent')) {
              <small id="scReferent-error" class="axa-field-error block mt-1" role="alert">Campo obbligatorio</small>
            }
          </div>

          <!-- 15. Numero interventi riparativi annui -->
          <div class="field col-12 md:col-4">
            <label for="repairInterventionsCount" class="font-semibold block mb-2">
              Numero interventi riparativi annui
              <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <input
              id="repairInterventionsCount"
              pInputText
              name="repairInterventionsCount"
              type="number"
              inputmode="numeric"
              [(ngModel)]="formData().otherFields.repairInterventionsCount"
              placeholder="Es. 500"
              class="w-full"
              [class.ng-invalid]="isFieldInvalid('repairInterventionsCount')"
              [attr.aria-required]="'true'"
              [attr.aria-describedby]="isFieldInvalid('repairInterventionsCount') ? 'repairInterventionsCount-error' : null"
            />
            @if (isFieldInvalid('repairInterventionsCount')) {
              <small id="repairInterventionsCount-error" class="axa-field-error block mt-1" role="alert">Campo obbligatorio</small>
            }
          </div>

          <!-- 23. Area commerciale AXA -->
          <div class="field col-12 md:col-4">
            <label for="axaCommercialArea" class="font-semibold block mb-2">
              Area commerciale AXA
              <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <input
              id="axaCommercialArea"
              pInputText
              name="axaCommercialArea"
              [(ngModel)]="formData().otherFields.axaCommercialArea"
              placeholder="Area commerciale..."
              class="w-full"
              [class.ng-invalid]="isFieldInvalid('axaCommercialArea')"
              [attr.aria-required]="'true'"
              [attr.aria-describedby]="isFieldInvalid('axaCommercialArea') ? 'axaCommercialArea-error' : null"
            />
            @if (isFieldInvalid('axaCommercialArea')) {
              <small id="axaCommercialArea-error" class="axa-field-error block mt-1" role="alert">Campo obbligatorio</small>
            }
          </div>

          <!-- 24. Da non pubblicare sul sito AXA -->
          <div class="field col-12 md:col-4">
            <label class="font-semibold block mb-2">
              Da non pubblicare sul sito AXA
              <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <div class="flex align-items-center gap-4 mt-1" role="radiogroup" aria-label="Da non pubblicare sul sito AXA">
              <div class="flex align-items-center gap-2">
                <p-radiobutton name="notPublishedInAxa" [value]="true" [(ngModel)]="formData().otherFields.notPublishedInAxa" inputId="notPublishedInAxa-si" />
                <label for="notPublishedInAxa-si" class="cursor-pointer">Sì</label>
              </div>
              <div class="flex align-items-center gap-2">
                <p-radiobutton name="notPublishedInAxa" [value]="false" [(ngModel)]="formData().otherFields.notPublishedInAxa" inputId="notPublishedInAxa-no" />
                <label for="notPublishedInAxa-no" class="cursor-pointer">No</label>
              </div>
            </div>
          </div>

          <!-- 25. Ex codice -->
          <div class="field col-12 md:col-4">
            <label for="exCode" class="font-semibold block mb-2">Ex codice</label>
            <input
              id="exCode"
              pInputText
              name="exCode"
              [(ngModel)]="formData().otherFields.exCode"
              placeholder="Ex codice..."
              class="w-full"
            />
          </div>

          <!-- 26. Mail per comunicazione -->
          <div class="field col-12 md:col-4">
            <label class="font-semibold block mb-2">
              Mail per comunicazione
              <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <div class="flex align-items-center gap-4 mt-1" role="radiogroup" aria-label="Mail per comunicazione"
              [attr.aria-invalid]="isFieldInvalid('communicationEmail')"
              [attr.aria-describedby]="isFieldInvalid('communicationEmail') ? 'communicationEmail-error' : null">
              <div class="flex align-items-center gap-2">
                <p-radiobutton name="communicationEmail" [value]="true" [(ngModel)]="formData().otherFields.communicationEmail" (ngModelChange)="onCommunicationEmailChange($event)" inputId="communicationEmail-si" />
                <label for="communicationEmail-si" class="cursor-pointer">Sì</label>
              </div>
              <div class="flex align-items-center gap-2">
                <p-radiobutton name="communicationEmail" [value]="false" [(ngModel)]="formData().otherFields.communicationEmail" (ngModelChange)="onCommunicationEmailChange($event)" inputId="communicationEmail-no" />
                <label for="communicationEmail-no" class="cursor-pointer">No</label>
              </div>
            </div>
            @if (isFieldInvalid('communicationEmail')) {
              <small id="communicationEmail-error" class="text-red-500" role="alert">Campo obbligatorio</small>
            }
          </div>

          <!-- 27. Ragione Sociale 2 -->
          <div class="field col-12 md:col-4">
            <label for="secondaryBusinessName" class="font-semibold block mb-2">Ragione Sociale 2</label>
            <input
              id="secondaryBusinessName"
              pInputText
              name="secondaryBusinessName"
              [(ngModel)]="formData().otherFields.secondaryBusinessName"
              placeholder="Ragione sociale 2..."
              class="w-full"
            />
          </div>

          <!-- 28. contract -->
          <div class="field col-12 md:col-4">
            <label for="contract" class="font-semibold block mb-2">contract</label>
            <input
              id="contract"
              pInputText
              name="contract"
              [(ngModel)]="formData().otherFields.contract"
              placeholder="contract..."
              class="w-full"
            />
          </div>

          <!-- 29. expert -->
          <div class="field col-12 md:col-4">
            <label for="expert" class="font-semibold block mb-2">expert</label>
            <input
              id="expert"
              pInputText
              name="expert"
              [(ngModel)]="formData().otherFields.expert"
              placeholder="expert..."
              class="w-full"
            />
          </div>

          <!-- 30. Vecchia mail -->
          <div class="field col-12 md:col-4">
            <label for="oldEmail" class="font-semibold block mb-2">Vecchia mail</label>
            <input
              id="oldEmail"
              pInputText
              name="oldEmail"
              type="email"
              [(ngModel)]="formData().otherFields.oldEmail"
              (blur)="touchedFields.add('oldEmail')"
              placeholder="vecchiaemail@esempio.com"
              class="w-full"
              [class.ng-invalid]="isFieldInvalid('oldEmail')"
              [attr.aria-describedby]="isFieldInvalid('oldEmail') ? 'oldEmail-error' : null"
            />
            @if (isFieldInvalid('oldEmail')) {
              <small id="oldEmail-error" class="axa-field-error block mt-1" role="alert">Formato email non valido</small>
            }
          </div>

        </div>
      </section>
    }

    <!-- =================== SEZIONE 3: INFORMAZIONI CARROZZERIA – Specifiche per network =================== -->
    @if (showNetworkFields()) {
      <section class="surface-0 border-round p-5 mb-4 shadow-1" role="region" aria-labelledby="network-section-heading">
        <div class="flex align-items-center gap-3 mb-4 pb-3 border-bottom-1 border-200">
          <i class="pi pi-sitemap text-2xl" style="color: #00125b;" aria-hidden="true"></i>
          <h3 id="network-section-heading" class="text-xl font-semibold m-0" style="color: #00125b;">Informazioni Carrozzeria – Specifiche per network</h3>
        </div>

        <div class="grid">

          <!-- 11. Nome network -->
          <div class="field col-12 md:col-6">
            <label for="networkName" class="font-semibold block mb-2">
              Nome network
              <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <input
              id="networkName"
              pInputText
              name="networkName"
              [(ngModel)]="formData().otherFields.networkName"
              (blur)="touchedFields.add('networkName')"
              placeholder="Nome del network"
              class="w-full"
              [class.ng-invalid]="isFieldInvalid('networkName')"
              [attr.aria-required]="'true'"
              [attr.aria-describedby]="isFieldInvalid('networkName') ? 'networkName-error' : null"
            />
            @if (isFieldInvalid('networkName')) {
              <small id="networkName-error" class="axa-field-error block mt-1" role="alert">Campo obbligatorio</small>
            }
          </div>

          <!-- 12. Descrizione network -->
          <div class="field col-12 md:col-6">
            <label for="networkDescription" class="font-semibold block mb-2">
              Descrizione
            </label>
            <input
              id="networkDescription"
              pInputText
              name="networkDescription"
              [(ngModel)]="formData().otherFields.networkDescription"
              placeholder="Descrizione del network"
              class="w-full"
            />
          </div>

          <!-- 13. Imprese per le quali lavora -->
          <div class="field col-12 md:col-4">
            <label for="assignedCompanies" class="font-semibold block mb-2">
              Imprese per le quali lavora
              <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <input
              id="assignedCompanies"
              pInputText
              name="assignedCompanies"
              [(ngModel)]="formData().otherFields.assignedCompanies"
              (blur)="touchedFields.add('assignedCompanies')"
              placeholder="Descrizione libera"
              class="w-full"
              [class.ng-invalid]="isFieldInvalid('assignedCompanies')"
              [attr.aria-required]="'true'"
              [attr.aria-describedby]="isFieldInvalid('assignedCompanies') ? 'assignedCompanies-error' : null"
            />
            @if (isFieldInvalid('assignedCompanies')) {
              <small id="assignedCompanies-error" class="axa-field-error block mt-1" role="alert">Campo obbligatorio</small>
            }
          </div>

          <!-- 14. Sinergie con società internazionali di tirabolli -->
          <div class="field col-12 md:col-4">
            <label for="synergiesInternational" class="font-semibold block mb-2">
              Sinergie con società internazionali di tirabolli
              <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <p-inputNumber
              inputId="synergiesInternational"
              name="synergiesInternationalPaintlessDentRepairCompaniesCount"
              [ngModel]="formData().otherFields.synergiesInternationalPaintlessDentRepairCompaniesCount"
              (ngModelChange)="onSynergiesInternationalChange($event)"
              (blur)="touchedFields.add('synergiesInternationalPaintlessDentRepairCompaniesCount')"
              [min]="0"
              [useGrouping]="false"
              placeholder="0"
              styleClass="w-full"
              [class.ng-invalid]="isFieldInvalid('synergiesInternationalPaintlessDentRepairCompaniesCount')"
              [attr.aria-required]="'true'"
              [attr.aria-describedby]="isFieldInvalid('synergiesInternationalPaintlessDentRepairCompaniesCount') ? 'synergiesInternational-error' : null"
            />
            @if (isFieldInvalid('synergiesInternationalPaintlessDentRepairCompaniesCount')) {
              <small id="synergiesInternational-error" class="axa-field-error block mt-1" role="alert">Campo obbligatorio</small>
            }
          </div>

          <!-- 15. Sinergie con società nazionali di tirabolli -->
          <div class="field col-12 md:col-4">
            <label for="synergiesNational" class="font-semibold block mb-2">
              Sinergie con società nazionali di tirabolli
              <span class="text-red-500" aria-hidden="true">*</span>
              <span class="visually-hidden">obbligatorio</span>
            </label>
            <p-inputNumber
              inputId="synergiesNational"
              name="synergiesNationalPaintlessDentRepairCompaniesCount"
              [ngModel]="formData().otherFields.synergiesNationalPaintlessDentRepairCompaniesCount"
              (ngModelChange)="onSynergiesNationalChange($event)"
              (blur)="touchedFields.add('synergiesNationalPaintlessDentRepairCompaniesCount')"
              [min]="0"
              [useGrouping]="false"
              placeholder="0"
              styleClass="w-full"
              [class.ng-invalid]="isFieldInvalid('synergiesNationalPaintlessDentRepairCompaniesCount')"
              [attr.aria-required]="'true'"
              [attr.aria-describedby]="isFieldInvalid('synergiesNationalPaintlessDentRepairCompaniesCount') ? 'synergiesNational-error' : null"
            />
            @if (isFieldInvalid('synergiesNationalPaintlessDentRepairCompaniesCount')) {
              <small id="synergiesNational-error" class="axa-field-error block mt-1" role="alert">Campo obbligatorio</small>
            }
          </div>

        </div>
      </section>
    }

    <!-- =================== PULSANTE SALVA =================== -->
    <div class="flex justify-content-end align-items-center gap-3 pt-3 border-top-1 border-200 mt-2">
      @if (formSubmitted() && hasValidationErrors()) {
        <p class="axa-field-error text-sm flex align-items-center gap-2 m-0" role="alert" aria-live="assertive">
          <i class="pi pi-exclamation-triangle" aria-hidden="true"></i>
          Completare i campi obbligatori prima di salvare.
        </p>
      }
      <button
        pButton
        type="submit"
        label="Salva Dati Integrativi"
        icon="pi pi-save"
        [loading]="isSaving()"
        [disabled]="isSaving()"
      ></button>
    </div>

  </form>
}
